import './RouteRules.scss';
import * as R from 'ramda';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { HiOutlinePlus } from 'react-icons/hi';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { Size } from '@Components/designConstants';
import { CopyButton } from '@Components/CopyButton';
import { useSetError } from '../../../redux/modals';
import { WtoLambdaFetch } from '../../../utils/fetch-utils';
import { CancelModal } from '../Modals/CancelModal';
import { ValidationErrors } from '../Validation/ValidationErrors';
import { formValidator } from '../Validation/wto-form-validator';
import { WtoTab, WtoTabType } from '../web-traffic-optimization-constants';
import { defaultRule, FormContext, formObjDefault } from '../wto-form-context';
import {
  buildPloverUrl,
  deriveFormEdited,
  formatDefaultId,
  formatRules,
  isSavedDraft,
  trimBackslashes
} from '../wto-utils';
import { DuplicateRuleModal } from './DuplicateRuleModal';
import { RuleCard } from './RuleCard';
import { RulesFormModal } from './RulesFormModal';

interface RouteRulesProps {
  goToTab: (tab: WtoTabType) => void;
  readOnly: boolean;
  setReadOnly: Dispatch<SetStateAction<boolean>>;
  formEdited: boolean;
  setFormEdited: Dispatch<SetStateAction<boolean>>;
  setSplitsEdited: Dispatch<SetStateAction<boolean>>;
  cancelConfirmation?: WtoTabType;
  setCancelConfirmation: Dispatch<SetStateAction<WtoTabType | undefined>>;
  userEmail: string;
  fetchPlovers: () => void;
  companyId: string;
  setModalData: (modalType: string, data: Plover) => void;
  plovers: Plover[];
}

export const RouteRules = ({
  goToTab,
  readOnly,
  setReadOnly,
  formEdited,
  setFormEdited,
  setSplitsEdited,
  cancelConfirmation,
  setCancelConfirmation,
  userEmail,
  fetchPlovers,
  companyId,
  setModalData,
  plovers
}: RouteRulesProps) => {
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [reorderEnabled, setReorderEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { wtoFormObj, setWtoFormObj, setNewRule } = useContext(FormContext);
  const [duplicateRuleIndex, setDuplicateRuleIndex] = useState(-1);
  const [showRuleCards, setShowRuleCards] = useState<boolean[]>(
    wtoFormObj.rules ? wtoFormObj.rules.map(() => true) : []
  );
  const [newRuleIndex, setNewRuleIndex] = useState<number | null>(null);
  const setError = useSetError();
  // confirmCancel resets wtoFormObj to formObjDefault (emptying default_id/default_url) in the same
  // tick it navigates away, which would otherwise make the effect below fire and redirect to Route
  // Details, racing with — and winning over — confirmCancel's own goToTab call. This ref suppresses
  // that specific redirect once a cancel has been confirmed; there's no need to reset it since this
  // component unmounts right after.
  const isCancellingRef = useRef(false);

  useEffect(() => {
    if (isCancellingRef.current) {
      return;
    }
    if (!wtoFormObj.default_id || !wtoFormObj.default_url) {
      goToTab(WtoTab.ROUTE_DETAILS);
    }
  }, [wtoFormObj, goToTab]);

  useEffect(() => {
    setReadOnly(wtoFormObj.status.toLocaleLowerCase() !== 'draft' && wtoFormObj.status !== '');
  }, [wtoFormObj, setReadOnly]);

  useEffect(() => {
    if (!showRulesModal) {
      setNewRuleIndex(null);
    }
  }, [showRulesModal]);

  const splitsEdited = useCallback(() => {
    const savedPlover = plovers.find(plover => plover.id === wtoFormObj.id);
    if (!savedPlover || savedPlover.status.toLocaleLowerCase() !== 'production') return false;
    return wtoFormObj.rules.some((rule, ruleIndex) => {
      const oldRule = savedPlover.rules[ruleIndex];
      return rule.variant_splits.some(
        (variant, variantIndex) => oldRule.variant_splits[variantIndex].split !== variant.split
      );
    });
  }, [plovers, wtoFormObj]);

  // formEdited is otherwise only derived by RouteDetailsForm's own effect, so reaching Rules
  // directly (e.g. the table's "Rules" quick action) would leave it at its stale default and
  // skip the unsaved-changes confirmation below.
  useEffect(() => {
    setFormEdited(deriveFormEdited(wtoFormObj));
  }, [wtoFormObj, setFormEdited]);

  useEffect(() => {
    setSplitsEdited(splitsEdited());
  }, [wtoFormObj, plovers, setSplitsEdited, splitsEdited]);

  // The effect above redirects away once default_id/default_url turn out to be missing, but an
  // effect only runs after this render has already painted — guard the render itself too, so the
  // invalid Rules content never flashes on screen while that redirect is in flight.
  if (!wtoFormObj.default_id || !wtoFormObj.default_url) {
    return <FullPageSpinner />;
  }

  const ploverUrl = buildPloverUrl(
    wtoFormObj.domain,
    wtoFormObj.folder_path,
    wtoFormObj.plover_url
  );

  const confirmCancel = () => {
    isCancellingRef.current = true;
    setWtoFormObj({ ...formObjDefault });
    setCancelConfirmation(undefined);
    goToTab(cancelConfirmation || WtoTab.ROUTE_TABLE);
  };

  const onCancelClick = () => {
    if ((!readOnly && formEdited) || (readOnly && splitsEdited())) {
      setCancelConfirmation(WtoTab.ROUTE_TABLE);
    } else {
      confirmCancel();
    }
  };

  const ruleModal = (index?: number) => {
    if (index === undefined) {
      setNewRule({
        ...R.clone(defaultRule),
        rule_order: wtoFormObj.rules.length.toString()
      });
    } else {
      setNewRuleIndex(index);
      setNewRule(wtoFormObj.rules[index]);
    }
    setShowRulesModal(true);
  };

  const hideCard = (index: number) => {
    const cardsShown = [...showRuleCards];
    cardsShown[index] = !cardsShown[index];
    setShowRuleCards(cardsShown);
  };

  const saveAsDraft = async () => {
    setLoading(true);
    try {
      const body = {
        company: companyId,
        test_name: wtoFormObj.test_name,
        utm_test_id: wtoFormObj.utm_test_id,
        default_url: wtoFormObj.default_url,
        default_id: formatDefaultId(wtoFormObj.default_id),
        plover_url: wtoFormObj.plover_url,
        folder_path: trimBackslashes(wtoFormObj.folder_path),
        notes: wtoFormObj.notes,
        variants: wtoFormObj.variants,
        domain: wtoFormObj.domain,
        rules: formatRules(wtoFormObj.rules),
        status: 'draft'
      };
      if (isSavedDraft(wtoFormObj)) {
        await WtoLambdaFetch<Record<string, unknown>>(`/plover/${wtoFormObj.id}`, {
          method: 'PATCH',
          params: { email: userEmail },
          body
        });
      } else {
        await WtoLambdaFetch<Record<string, unknown>>('/plover', {
          method: 'POST',
          params: { email: userEmail },
          body
        });
      }
      fetchPlovers();
      goToTab(WtoTab.ROUTE_TABLE);
      setCancelConfirmation(undefined);
    } catch (e) {
      setError({
        message: `Failed to submit route. Error: ${e.message}`,
        reportError: e
      });
    }
    setLoading(false);
  };

  const validateForm = (isDraft?: boolean) => {
    const errors = formValidator(
      wtoFormObj,
      [
        'test_name',
        'utm_test_id',
        'variants',
        'default_id',
        'default_url',
        'plover_url',
        'folder_path',
        'domain',
        'rules-maximum',
        'rules-minimum'
      ],
      {
        isBulkPlover: false
      }
    );
    if (errors.length > 0) {
      setValidationErrors(errors);
    } else if (isDraft) {
      saveAsDraft();
    } else {
      formatRules(wtoFormObj.rules);
      setModalData(splitsEdited() ? 'Update Route' : 'Create Route', wtoFormObj);
    }
  };

  return (
    <div className='route-rules-container' data-testid='route-rules'>
      <div className='route-rules'>
        <div className='description'>
          <p>
            Set up rules below to direct traffic to your variants. Rules will be executed in
            top-down order, first rule that matches a condition will redirect the user. If no rules
            were matched, then user will be redirected to the fallback variant. To change Rule
            Execution Order, toggle the &quot;Reorder&quot; switch below.
          </p>
          <div className='route-url-wrapper'>
            <p>
              Route URL: <span className='route-url'>{ploverUrl}</span>
            </p>
            <CopyButton text={ploverUrl} tooltipLabel='Copy URL to Clipboard' />
          </div>
        </div>
        {loading ? (
          <FullPageSpinner />
        ) : (
          <div className='rules-editor'>
            <div className='rules-header'>
              <h3>Rules</h3>
              {wtoFormObj.rules.length > 0 && (
                <div className='rules-toolbar'>
                  <ToggleSwitch
                    className='reorder-toggle'
                    label='Reorder'
                    checked={reorderEnabled}
                    onChange={setReorderEnabled}
                    disabled={readOnly}
                  />
                  <Button
                    size={Size.LG}
                    leadingElement={<HiOutlinePlus />}
                    onClick={() => {
                      setValidationErrors([]);
                      ruleModal();
                    }}
                    disabled={readOnly}
                  >
                    Add Rule
                  </Button>
                </div>
              )}
            </div>
            <ValidationErrors validationErrors={validationErrors} />
            {wtoFormObj.rules.length === 0 ? (
              <div className='rule-prompt'>
                Get started by adding your first rule!
                <Button
                  size={Size.LG}
                  leadingElement={<HiOutlinePlus />}
                  onClick={() => {
                    setValidationErrors([]);
                    ruleModal();
                  }}
                  disabled={readOnly}
                >
                  Add Rule
                </Button>
              </div>
            ) : (
              wtoFormObj.rules.map((rule, index) => (
                <RuleCard
                  key={index}
                  data={rule}
                  index={index}
                  show={showRuleCards[index]}
                  hideCard={hideCard}
                  ruleModal={ruleModal}
                  reorderEnabled={reorderEnabled}
                  setDuplicateRuleIndex={setDuplicateRuleIndex}
                  showRuleCards={showRuleCards}
                  setShowRuleCards={setShowRuleCards}
                />
              ))
            )}
            <div className='rule-card default'>
              <span className='rule-name'>Default Rule</span>
              <hr />
              <div className='rule-spec-body rule-spec-row plover'>
                <div className='rule-spec-col plover'>
                  {wtoFormObj.default_id}
                  <span className='light-text'>{wtoFormObj.default_url}</span>
                </div>
                <div className='plover-percentage'>100%</div>
              </div>
            </div>
          </div>
        )}
        {showRulesModal && (
          <RulesFormModal
            show={showRulesModal}
            closeModal={() => setShowRulesModal(false)}
            readOnly={readOnly}
            goToTab={goToTab}
            ruleIndex={newRuleIndex}
            showRuleCards={showRuleCards}
            setShowRuleCards={setShowRuleCards}
            isCancellingRef={isCancellingRef}
          />
        )}
        <div className='rules-buttons'>
          <Button size={Size.LG} onClick={onCancelClick}>
            Cancel
          </Button>
          <Button size={Size.LG} onClick={() => validateForm(true)} disabled={readOnly}>
            Save as Draft
          </Button>
          <Button
            size={Size.LG}
            onClick={() => validateForm(false)}
            disabled={readOnly && !splitsEdited()}
          >
            {wtoFormObj.status.toLocaleLowerCase() !== 'draft' ? 'Update Route' : 'Create Route'}
          </Button>
        </div>
      </div>
      <DuplicateRuleModal
        show={duplicateRuleIndex > -1}
        closeModal={() => setDuplicateRuleIndex(-1)}
        index={duplicateRuleIndex}
        showRuleCards={showRuleCards}
        setShowRuleCards={setShowRuleCards}
      />
      <CancelModal
        show={Boolean(cancelConfirmation)}
        closeModal={() => setCancelConfirmation(undefined)}
        confirmCancel={confirmCancel}
      />
    </div>
  );
};
